import { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import type { CostReportItem } from '@/services/reports.service';

function formatCurrency(n: number) {
  return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

interface ChartDataItem {
  name: string;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
}

const TOP_N = 10;

function getBarColor(margin: number) {
  if (margin >= 25) return '#22c55e'; // green-500
  if (margin >= 12) return '#eab308'; // yellow-500
  if (margin >= 0) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0]?.payload as ChartDataItem;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs min-w-[160px]">
      <p className="font-semibold text-gray-800 mb-2 truncate max-w-[200px]">{label}</p>
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-blue-600">Ventas</span>
          <span className="font-medium">{formatCurrency(item.revenue)}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-orange-600">Costo</span>
          <span className="font-medium">{formatCurrency(item.cost)}</span>
        </div>
        <div className="border-t border-gray-100 pt-1 flex justify-between gap-4">
          <span className="text-green-600 font-medium">Utilidad</span>
          <span className="font-bold">{formatCurrency(item.profit)}</span>
        </div>
        <div className="flex justify-between gap-4 text-[10px]">
          <span className="text-gray-400">Margen</span>
          <span className="font-semibold">{item.margin.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

interface ProfitChartProps {
  data: CostReportItem[];
  groupBy: string;
}

export function ProfitChart({ data, groupBy }: ProfitChartProps) {
  const [viewMode, setViewMode] = useState<'profit' | 'margin'>('profit');

  const chartData = useMemo<ChartDataItem[]>(() => {
    if (!data || data.length === 0) return [];

    return [...data]
      .sort((a, b) => b.profit - a.profit)
      .slice(0, TOP_N)
      .map((item) => {
        const label = item?.label || 'Sin nombre';
        return {
          name:
            groupBy === 'day'
              ? new Date(label + 'T00:00:00').toLocaleDateString('es-MX', {
                  day: '2-digit',
                  month: 'short',
                })
              : groupBy === 'month'
                ? new Date(label + '-01T00:00:00').toLocaleDateString('es-MX', {
                    month: 'short',
                    year: '2-digit',
                  })
                : label.length > 18
                  ? label.slice(0, 16) + '…'
                  : label,
          revenue: Number(item.revenue) || 0,
          cost: Number(item.cost) || 0,
          profit: Number(item.profit) || 0,
          margin: Number(item.margin) || 0,
        };
      });
  }, [data, groupBy]);

  if (chartData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-400">
        <TrendingUp size={36} className="mb-2 opacity-50" />
        <p className="text-sm font-medium">Sin datos para graficar</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">
            Top {Math.min(TOP_N, chartData.length)} — {groupBy === 'day' ? 'Días' : groupBy === 'month' ? 'Meses' : 'Productos'}
            {' '}con mayor utilidad
          </h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Barras verdes = utilidad · Naranjas = costo
          </p>
        </div>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('profit')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
              viewMode === 'profit'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Utilidad
          </button>
          <button
            onClick={() => setViewMode('margin')}
            className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-all ${
              viewMode === 'margin'
                ? 'bg-white text-gray-800 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Margen
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="h-[320px] min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          {viewMode === 'profit' ? (
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }} barGap={2} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#f0f0f0' }}
                tickLine={false}
                interval={0}
                angle={chartData.length > 6 ? -20 : 0}
                textAnchor={chartData.length > 6 ? 'end' : 'middle'}
                height={50}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <Legend
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              <Bar dataKey="cost" name="Costo" stackId="a" fill="#f97316" radius={[0, 0, 0, 0]} opacity={0.7} />
              <Bar dataKey="profit" name="Utilidad" stackId="a" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.margin)} />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 8, right: 24, left: 8, bottom: 8 }}
              barCategoryGap="25%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#f0f0f0' }}
                tickLine={false}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 100]}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={100}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const item = payload[0]?.payload as ChartDataItem;
                  return (
                    <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 text-xs min-w-[140px]">
                      <p className="font-semibold text-gray-800 mb-1">{item.name}</p>
                      <div className="flex justify-between gap-3">
                        <span className="text-gray-400">Margen</span>
                        <span className="font-bold">{item.margin.toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between gap-3 mt-0.5 text-[10px]">
                        <span className="text-blue-600">{formatCurrency(item.revenue)}</span>
                        <span className={item.profit >= 0 ? 'text-green-600' : 'text-red-500'}>
                          {formatCurrency(item.profit)}
                        </span>
                      </div>
                    </div>
                  );
                }}
                cursor={{ fill: 'rgba(0,0,0,0.03)' }}
              />
              <Bar dataKey="margin" name="Margen %" radius={[0, 4, 4, 0]}>
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.margin >= 25 ? '#22c55e' : entry.margin >= 12 ? '#eab308' : entry.margin >= 0 ? '#f97316' : '#ef4444'}
                  />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Legend inline */}
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] text-gray-400">
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#22c55e' }} />
          Margen ≥ 25%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#eab308' }} />
          12–25%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#f97316' }} />
          0–12%
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: '#ef4444' }} />
          Pérdida
        </span>
      </div>
    </div>
  );
}
